/**
 * referral-api.ts — API client for the admin referral queue endpoints.
 *
 * All endpoints route through the REST gateway's per-service admin group
 * (`/v1/integrations/admin` -> `/admin`, Epic 12.3) to betty-integrations. Gateway
 * strips `/v1/integrations`; service sees `/admin/referrals/*` (ReferralQueueAdminController
 * remounted from `v1/admin/referrals` in the same review-fix — see that controller's
 * header comment for why).
 *   POST /v1/integrations/admin/referrals/search      — list/filter (PHI in body)
 *   GET  /v1/integrations/admin/referrals/:id         — single detail
 *   POST /v1/integrations/admin/referrals/:id/confirm-provider
 *   POST /v1/integrations/admin/referrals/:id/reject
 *   POST /v1/integrations/admin/referrals/:id/notify-ineligible
 *
 * PHI constraint: search criteria in POST body (not query params) to keep
 * patient name out of CloudFront/ALB access logs.
 *
 * Auth: staff JWT attached via Authorization header (Cognito OIDC).
 * Token is obtained from the auth context if available. The hook-based
 * callers (useReferralQueue, useReferralActions) provide the token.
 */

import { SERVICES_BASE_URL } from '../../../lib/services-base.js';

export interface PendingReferralSummary {
  referralId: string;
  patientName: { firstName: string; lastName: string };
  submissionDate: string; // ISO 8601 UTC
  providerName: string;
  providerNpi: string;
  referralSource: 'direct' | 'access_registry' | 'member_share';
  status: 'awaiting_provider_review' | 'eligibility_pending' | 'ineligible' | 'rejected';
}

export interface ProviderDetail {
  firstName: string;
  lastName: string;
  npi: string;
  practice: string;
  email: string | null;
  phone: string | null;
  specialty: string | null;
  address: string | null;
}

export interface PendingReferralDetail {
  referralId: string;
  patientName: { firstName: string; lastName: string };
  submissionDate: string;
  providerName: string;
  providerNpi: string;
  referralSource: 'direct' | 'access_registry' | 'member_share';
  status: 'awaiting_provider_review' | 'eligibility_pending' | 'ineligible' | 'rejected';
  provider: ProviderDetail;
  providerSource: 'nppes' | 'manual';
  cmsReasonCode: string | null;
  rejectionReason: string | null;
  notes: string | null;
}

export interface AdminReferralListResponse {
  items: PendingReferralSummary[];
  nextCursor: string | null;
  totalCount: number;
}

export type ReferralQueueStatus = 'awaiting_provider_review' | 'rejected' | 'ineligible';

// ─── API helper ───────────────────────────────────────────────────────────────

async function apiFetch(path: string, options?: RequestInit, token?: string): Promise<Response> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options?.headers ?? {}),
  };
  return fetch(`${SERVICES_BASE_URL}/v1/integrations${path}`, {
    ...options,
    headers,
  });
}

async function parseRfc9457Error(response: Response): Promise<Error> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/problem+json')) {
    try {
      const problem = (await response.json()) as { title?: string; detail?: string };
      return new Error(problem.detail ?? problem.title ?? `HTTP ${String(response.status)}`);
    } catch {
      // fall through
    }
  }
  return new Error(`Request failed with status ${String(response.status)}`);
}

// ─── API functions ────────────────────────────────────────────────────────────

export const referralApi = {
  async searchReferrals(
    params: { status?: ReferralQueueStatus; search?: string | null },
    token?: string,
  ): Promise<AdminReferralListResponse> {
    const response = await apiFetch(
      '/admin/referrals/search',
      {
        method: 'POST',
        body: JSON.stringify({
          status: params.status ?? 'awaiting_provider_review',
          search: params.search ?? null,
        }),
      },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as AdminReferralListResponse;
  },

  async getReferral(referralId: string, token?: string): Promise<PendingReferralDetail> {
    const response = await apiFetch(`/admin/referrals/${referralId}`, { method: 'GET' }, token);
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as PendingReferralDetail;
  },

  async confirmProvider(
    referralId: string,
    token?: string,
  ): Promise<{ referralId: string; status: 'eligibility_pending' }> {
    const response = await apiFetch(
      `/admin/referrals/${referralId}/confirm-provider`,
      { method: 'POST' },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as { referralId: string; status: 'eligibility_pending' };
  },

  async rejectReferral(
    referralId: string,
    reason: string,
    token?: string,
  ): Promise<{ referralId: string; status: 'rejected' }> {
    const response = await apiFetch(
      `/admin/referrals/${referralId}/reject`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as { referralId: string; status: 'rejected' };
  },

  async notifyIneligible(
    referralId: string,
    token?: string,
  ): Promise<{ referralId: string; notificationSent: true }> {
    const response = await apiFetch(
      `/admin/referrals/${referralId}/notify-ineligible`,
      { method: 'POST' },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as { referralId: string; notificationSent: true };
  },
};
